import { normalizeStage } from '../data/dealStages.js';


export const BUY_SIDE_COMMISSION_PCT = 0.40;
export const SELL_SIDE_COMMISSION_PCT = 0.10;
export const PARACLETE_BUY_SIDE_COMMISSION_PCT = 0.50;

// The Operating Fund's 20% of margin, and the 5% a named disposition
// manager takes out of it.
//
// The 5% ran globally from 2026-08-11 and was withdrawn on 2026-09-15
// because nobody was ever paid under it: it applied to every closing
// deal whether or not anyone had done the work, and it was hard-wired
// to one person. It is back as an ASSIGNMENT. Name somebody in
// Team and Closing and that deal carves 5% for them; leave it empty — as
// every existing deal is — and the fund keeps the whole 20%.
//
// The carve-out comes out of the fund rather than on top of it, so the
// shares still account for exactly 100% of margin either way. That is
// the invariant worth protecting here; test-coordinator-commissions
// asserts it with and without a manager assigned.
export const OPERATING_FUND_COMMISSION_PCT = 0.20;
export const DISPOSITION_MANAGER_COMMISSION_PCT = 0.05;

// The Paraclete representative on an investor deal.
//
// It occupies the SELL-SIDE slot rather than sitting on top of the
// shares, because on an investor deal there is no outside sale to
// manage — the investor is the buyer, and the rep who owns that
// relationship is the person doing the sell-side job. At the agreed
// 10% the allocation is identical to a normal deal's, which is the
// whole reason to put it there: no rate had to move to make room.
//
// The rate is per investor (investor_profiles.rep_commission_rate,
// defaulting to this) because it will not stay uniform forever.
export const INVESTOR_REP_COMMISSION_PCT = 0.10;

// The title fee, taken off every transaction before anybody's
// percentage is worked out.
//
// A flat amount, not a rate: title work costs what it costs whether the
// margin is four figures or six. Taking it off the TOP rather than out
// of one share is what makes that true — every share below is a
// percentage of what is left, so the cost lands on everyone in
// proportion rather than on whichever pool it was subtracted from.
//
// $500 from 2026-10-02; it was $200 before. Deals that closed under the
// old fee KEEP it. The fee is subtracted before every single share, so
// raising it across the board would quietly shrink every payout on every
// deal already closed — numbers people have been paid against and that
// the materialized commission_payouts rows still hold. Same shape of
// gate, and the same reason, as the coordinator margin-split date below.
//
// And per deal this is only the DEFAULT. A deal can carry its own
// amount — see titleFeeAmountForDeal — because a title order with four
// counties and a probate in it does not cost what a one-tract clean
// chain costs, and a flat fee nobody can change gets worked around in a
// spreadsheet instead of recorded on the deal.
export const TITLE_FEE_PER_TRANSACTION = 500;
export const TITLE_FEE_EFFECTIVE_DATE = '2026-10-02';
export const TITLE_FEE_BEFORE_EFFECTIVE_DATE = 200;

// The stages at which a deal's commissions have actually been divided —
// the same pair buildCommissionPayoutRows writes its rows at. Only these
// keep the old fee; everything still being worked pays today's.
export const TITLE_FEE_SETTLED_STAGES = new Set(['Closed', 'Funded']);

// Who the fee is paid to. Named rather than left as an unattributed
// deduction, because a line on a commission email that says only "title
// fee" is a number nobody can chase.
export const TITLE_FEE_RECIPIENT = 'Shadowland Resources, LLC';

/**
 * What THIS deal's title fee is, before the margin cap.
 *
 * Three answers, in order:
 *   1. `lifecycle.titleFeeAmount` — set on the deal. The Settlement
 *      Statement's "Title Closing Cost" field writes here, so changing
 *      it there moves the whole waterfall: every percentage below is a
 *      slice of what is left after it.
 *   2. The old $200, on a deal ALREADY DIVIDED under it.
 *   3. The current $500.
 *
 * On ALREADY DIVIDED rather than on the close date alone, which is what
 * it used to test. The point of grandfathering is that raising the fee
 * must not retroactively shrink payouts that have already gone out —
 * and a deal still in Under Contract or Processing has had nothing go
 * out. Eleven live deals carried an acquisition close date from August
 * or September, so each was heading for a division at $200 while the
 * desk was charging $500, and the only remedy was somebody remembering
 * to type the fee onto all eleven by hand.
 *
 * Divided means the commission rows exist, which buildCommissionPayoutRows
 * writes at Closed or Funded and nowhere else — the same pair of stages,
 * so the two cannot disagree about which deals are settled.
 *
 * An override of 0 is honoured — a deal where we did the title work
 * in-house charges nothing — which is why this tests `>= 0` rather than
 * truthiness. Anything unparseable falls through to the default rather
 * than zeroing the fee: a stray string in the JSONB must not silently
 * hand everybody a bigger share.
 */
export function titleFeeAmountForDeal(deal = {}) {
  // `Number(null)`, `Number('')` and `Number([])` are all 0 and all
  // finite, so a bare Number() here would read an absent or cleared
  // override as a $0 fee — and a $0 fee hands everybody below a bigger
  // share than they earned. Only a real number, or a string with a
  // number in it, counts.
  const raw = deal?.lifecycle?.titleFeeAmount;
  const usable = typeof raw === 'number' || (typeof raw === 'string' && raw.trim() !== '');
  const override = usable ? Number(raw) : Number.NaN;
  if (Number.isFinite(override) && override >= 0) return override;
  // Both halves, or the rule is the wrong one: a deal has to be settled
  // AND have been settled before the increase.
  if (!TITLE_FEE_SETTLED_STAGES.has(normalizeStage(deal?.stage))) return TITLE_FEE_PER_TRANSACTION;
  const closeDate = String(dealCommissionCloseDate(deal || {}) || '').slice(0, 10);
  if (closeDate && closeDate < TITLE_FEE_EFFECTIVE_DATE) return TITLE_FEE_BEFORE_EFFECTIVE_DATE;
  return TITLE_FEE_PER_TRANSACTION;
}

/**
 * What the title fee actually takes from this deal, and what is left
 * to divide. Exported because the commission card, the report and the
 * ledger must all show the same two numbers.
 *
 * Capped at the margin, and never negative. A deal that made $80
 * pays $80 and the shares are all zero; a deal that made nothing, or
 * lost money, pays nothing. A fee larger than the margin would push
 * the shares negative, which in the ledger is a payout owed BY the
 * recipient — a bill nobody agreed to.
 *
 * `deal` is optional and only decides WHICH fee applies. Called without
 * one you get the current default, which is what a caller asking "what
 * does a transaction cost" means.
 */
/**
 * The other costs that come off a closing — attorney's fees, recording,
 * couriers — as typed on the settlement statement.
 *
 * A list rather than one more named field, because the next one is
 * always a different name, and a statement that can only hold "the
 * attorney fee" sends the courier charge back to the spreadsheet this
 * worksheet exists to replace.
 *
 * Read with the same care as the title override above: `Number('')` and
 * `Number(null)` are both a finite 0, so a half-typed row must not read
 * as a real $0 line. Only a genuine number counts, and a negative one is
 * dropped — a cost that pays money INTO the pool is not a cost, and
 * would hand everybody below a bigger share than they earned.
 */
export function dealClosingCosts(deal = {}) {
  const raw = deal?.lifecycle?.closingCosts;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry, index) => {
      const value = entry?.amount;
      const usable = typeof value === 'number' || (typeof value === 'string' && String(value).trim() !== '');
      const amount = usable ? Number(value) : Number.NaN;
      return {
        id: String(entry?.id || `cost-${index}`),
        label: String(entry?.label || '').trim(),
        payeeLegalName: String(entry?.payeeLegalName || '').trim(),
        amount: Number.isFinite(amount) && amount > 0 ? amount : 0,
      };
    })
    .filter((entry) => entry.amount > 0 || entry.label);
}

export function closingCostsTotal(deal = {}) {
  return dealClosingCosts(deal).reduce((sum, entry) => sum + entry.amount, 0);
}

/**
 * What the title fee and the other closing costs take from this deal,
 * and what is left to divide. Exported because the commission card, the
 * report, the ledger and the settlement statement must all show the
 * same numbers.
 *
 * Capped at the margin, and never negative. A deal that made $80
 * pays $80 and the shares are all zero; a deal that made nothing, or
 * lost money, pays nothing. A fee larger than the margin would push
 * the shares negative, which in the ledger is a payout owed BY the
 * recipient — a bill nobody agreed to.
 *
 * The title fee is taken FIRST and the other costs share what is left,
 * so a thin deal degrades the same way it always did rather than
 * changing what title collects because somebody added an attorney line.
 * Each cost is scaled by the same fraction when there is not enough to
 * pay them all, so no single line is silently zeroed while the rest are
 * paid in full.
 *
 * `deal` is optional and only decides WHICH fee applies. Called without
 * one you get the current default, which is what a caller asking "what
 * does a transaction cost" means.
 */
export function titleFeeForMargin(margin, deal = {}) {
  const total = Number(margin) || 0;
  if (total <= 0) {
    return { titleFee: 0, closingCosts: [], closingCostsTotal: 0, distributableMargin: total };
  }
  const titleFee = Math.min(titleFeeAmountForDeal(deal), total);
  const afterTitle = total - titleFee;
  const requested = dealClosingCosts(deal).filter((entry) => entry.amount > 0);
  const requestedTotal = requested.reduce((sum, entry) => sum + entry.amount, 0);
  // Proportional when the margin cannot cover them all, exact otherwise.
  const scale = requestedTotal > afterTitle && requestedTotal > 0 ? afterTitle / requestedTotal : 1;
  const closingCosts = requested.map((entry) => ({ ...entry, amount: entry.amount * scale }));
  const costsTotal = closingCosts.reduce((sum, entry) => sum + entry.amount, 0);
  return {
    titleFee,
    closingCosts,
    closingCostsTotal: costsTotal,
    distributableMargin: afterTitle - costsTotal,
  };
}

// Coordinator rates are percentages OF MARGIN carved out of the 40%
// buy-side pool: Sydney's 25% of margin leaves 15% of margin for the
// buy-side manager. Effective 2026-08-14 — payouts materialized before
// then applied the rate to the pool itself (25% of pool = 10% of
// margin), and the live ledger recomputes shares per deal, so the date
// gate keeps closed-deal history stable.
export const COORDINATOR_COMMISSION_RATES = {
  sydney: 0.25,
  gabby: 0.10,
};
export const COORDINATOR_MARGIN_SPLIT_EFFECTIVE_DATE = '2026-08-14';

// Team positions selectable at invite time. The position says HOW a
// person's commission applies; the profile's commission_rate (a
// fraction of margin) says how much, where the position allows a
// per-person rate.
export const TEAM_POSITIONS = [
  { value: 'buy_side_agent', label: 'Buy-Side Agent (Landman)', rateEditable: false, defaultRate: null, how: 'Paid the remainder of the 40% buy-side pool after the coordinator’s carve-out.' },
  { value: 'coordinator', label: 'Transaction Coordinator', rateEditable: true, defaultRate: 0.10, how: 'Their % of margin is carved out of the 40% buy-side pool.' },
  { value: 'sell_side_agent', label: 'Sell-Side (Disposition) Agent', rateEditable: true, defaultRate: 0.10, how: 'Straight % of deal margin on the sell side.' },
  { value: 'disposition_manager', label: 'Disposition Manager', rateEditable: false, defaultRate: DISPOSITION_MANAGER_COMMISSION_PCT, how: 'Fixed 5% of margin, out of the Operating Fund, on deals where they are named in Team and Closing.' },
  { value: 'firm_partner', label: 'Firm Partner', rateEditable: false, defaultRate: 0.10, how: 'Fixed 10% partner share of margin.' },
  { value: 'operations', label: 'Operations / Support', rateEditable: false, defaultRate: null, how: 'No deal commission.' },
];

export function teamPositionMeta(value) {
  return TEAM_POSITIONS.find((p) => p.value === String(value || '')) || null;
}

// A profile's own commission rate — a fraction of margin. 0 when unset
// or nonsense, so callers can fall back to their defaults.
export function profileCommissionRate(profile = {}) {
  const rate = Number(profile.commissionRate);
  if (!Number.isFinite(rate) || rate <= 0) return 0;
  return Math.min(1, rate);
}

export function personKey(profile = {}) {
  const name = String(profile.name || profile.fullName || '').trim().toLowerCase();
  const email = String(profile.email || '').trim().toLowerCase();
  const first = name.split(/\s+/)[0] || '';
  if (first) return first;
  return email.split('@')[0].split(/[._+-]/)[0] || '';
}

export function coordinatorCommissionRateForUser(profile = {}) {
  // A rate set on the profile at invite/edit time wins; the legacy
  // first-name table stays as the fallback for pre-existing
  // coordinators who were never stamped with one.
  if (String(profile.position || '') === 'coordinator') {
    const rate = profileCommissionRate(profile);
    if (rate) return rate;
  }
  return COORDINATOR_COMMISSION_RATES[personKey(profile)] || 0;
}

/**
 * Does this person COORDINATE? Not: what are they paid.
 *
 * Deal creation has to know the difference. A coordinator sources the
 * lead and leaves the acquisition open for somebody to claim; an
 * acquisition manager keeps the deal they made. Asking
 * coordinatorCommissionRateForUser > 0 answers that correctly today —
 * every coordinator on the roster carries a rate — and fails silently
 * the first time somebody is invited as a coordinator and their rate
 * has not been entered yet. That person would be stamped as the
 * buy-side agent on their own deals and nobody could claim them.
 *
 * So: the position, which is the field that says what they do, OR the
 * legacy first-name table, which is how coordinators who predate the
 * position column are still recognised.
 */
export function isCoordinator(profile = {}) {
  if (String(profile?.position || '') === 'coordinator') return true;
  return coordinatorCommissionRateForUser(profile) > 0;
}

// Sell-side rate for a deal: the assigned sell agent's per-profile
// rate when they're stamped as a sell-side agent, else the standard
// 10% of margin.
export function sellSideCommissionRateForDeal(deal = {}, staffUsers = []) {
  const agent = (staffUsers || []).find((user) => (
    user?.id && String(user.id) === String(deal.sellSideAgentId || '')
  ));
  if (agent && String(agent.position || '') === 'sell_side_agent') {
    const rate = profileCommissionRate(agent);
    if (rate) return rate;
  }
  return SELL_SIDE_COMMISSION_PCT;
}

// 5% when this deal names a disposition manager, 0 when it does not.
// Optional by construction: no assignment, no carve-out, and the
// Operating Fund is untouched.
export function dispositionManagerCommissionRateForDeal(deal = {}) {
  return deal?.dispositionManagerId ? DISPOSITION_MANAGER_COMMISSION_PCT : 0;
}

// The investor this deal is going to, if one has been chosen.
//
// Deliberately NOT isInvestorPlaced. A placement needs the entity too,
// because the deed has to name a grantee — but the sell-side commission
// is decided the moment somebody picks the investor, and the entity
// often lands days later. Gating the money on the entity is what kept
// every Matt Sanchez deal paying the old buyer's sell-side agent: no
// investor entity exists on this book yet, so no deal was ever "placed"
// and the rep share never fired once.
function chosenInvestorId(deal = {}) {
  return String(deal?.investorId || '').trim() || null;
}

function investorRecord(deal = {}, investors = []) {
  const id = chosenInvestorId(deal);
  if (!id) return null;
  return (investors || []).find((i) => i?.id && String(i.id) === id) || null;
}

// The rep's rate on this deal: the investor's own, or the agreed 10%.
//
// The roster is admin-only, so a non-admin screen computing shares has an
// empty list and cannot look the investor up. It still gets the agreed
// rate, so those screens show the right amount instead of a silent zero.
export function investorRepCommissionRateForDeal(deal = {}, investors = []) {
  if (!chosenInvestorId(deal)) return 0;
  const rate = Number(investorRecord(deal, investors)?.repCommissionRate);
  if (Number.isFinite(rate) && rate > 0) return Math.min(1, rate);
  return INVESTOR_REP_COMMISSION_PCT;
}

/**
 * Who the rep share is paid to, or null.
 *
 * This is the sell-side agent on an investor deal: the user tied to the
 * investor on the investor's own record. Null without the roster, which
 * is why the replacement below is gated on this id and not on the rate.
 */
export function investorRepIdForDeal(deal = {}, investors = []) {
  return investorRecord(deal, investors)?.assignedRepId || null;
}

/**
 * The sell-side agent for a deal.
 *
 * On an investor deal it is the user tied to that investor, not whoever
 * was stamped on the deal when it was still going to a buyer. Falls back
 * to the deal's own sell-side agent otherwise, and also when the
 * investor has no rep assigned — see the note on the replacement guard.
 */
export function sellSideAgentIdForDeal(deal = {}, investors = []) {
  return investorRepIdForDeal(deal, investors) || deal?.sellSideAgentId || null;
}

export function coordinatorCommissionRateForDeal(deal = {}, staffUsers = []) {
  const explicit = Number(deal.coordinatorCommissionPct);
  if (Number.isFinite(explicit) && explicit > 0) {
    return Math.min(1, explicit);
  }
  const coordinator = (staffUsers || []).find((user) => (
    user?.id && String(user.id) === String(deal.coordinatorId || '')
  ));
  return coordinatorCommissionRateForUser(coordinator);
}

export function dealCommissionCloseDate(deal = {}) {
  return deal.sellClosingDate || deal.buyClosingDate || deal.closingDate || null;
}

// True when the deal closes under the margin-based coordinator policy.
export function coordinatorMarginSplitApplies(deal = {}) {
  const closeDate = String(dealCommissionCloseDate(deal) || '').slice(0, 10);
  if (!closeDate) return true;
  return closeDate >= COORDINATOR_MARGIN_SPLIT_EFFECTIVE_DATE;
}

export function splitBuySideCommission(poolAmount, coordinatorRate = 0, options = {}) {
  const pool = Number(poolAmount) || 0;
  const rate = Math.min(1, Math.max(0, Number(coordinatorRate) || 0));
  const margin = Number(options.margin);
  const marginBased = options.marginBased !== false && Number.isFinite(margin);
  let coordinator = (marginBased ? margin : pool) * rate;
  // The coordinator's carve-out can never exceed the pool it comes from.
  if (pool >= 0) coordinator = Math.min(pool, coordinator);
  return {
    buySideAgent: pool - coordinator,
    coordinator,
    coordinatorRate: rate,
    coordinatorMarginBased: marginBased,
  };
}

export function buildStandardDealCommissionShares(margin, deal = {}, staffUsers = [], options = {}) {
  const grossMargin = Number(margin) || 0;
  // The title fee comes off the top. Everything below is a percentage
  // of what is left, so `totalMargin` is the DISTRIBUTABLE margin from
  // here on and the shares still account for 100% of it.
  const { titleFee, closingCosts, closingCostsTotal: costsCharged, distributableMargin: totalMargin } = titleFeeForMargin(grossMargin, deal);
  const buySidePool = totalMargin * BUY_SIDE_COMMISSION_PCT;
  const coordinatorRate = deal?.coordinatorId
    ? coordinatorCommissionRateForDeal(deal, staffUsers)
    : 0;
  const buySplit = splitBuySideCommission(buySidePool, coordinatorRate, {
    margin: totalMargin,
    marginBased: coordinatorMarginSplitApplies(deal),
  });
  // On an investor deal the rep takes the sell-side slot: there is no
  // outside sale to manage, so paying both would allocate that 10%
  // twice. `sellSideReplacedByInvestorRep` is on the result so the
  // commission card can say so rather than a sell-side agent silently
  // reading zero.
  const investorRepRate = investorRepCommissionRateForDeal(deal, options.investors);
  const investorRepId = investorRepIdForDeal(deal, options.investors);
  // The rep TAKES the sell-side slot rather than adding a share, so the
  // allocation on an investor deal at the agreed 10% is byte-for-byte a
  // normal deal's.
  //
  // But only when there is somebody to pay. An investor with no rep
  // assigned would otherwise zero the sell side and credit the rep share
  // to nobody — the agent's commission would vanish rather than move.
  // No recipient, no replacement: the deal keeps its own sell-side agent
  // until a rep is tied to the investor.
  const repTakesSellSide = investorRepRate > 0 && !!investorRepId;
  const sellSideRate = repTakesSellSide ? 0 : sellSideCommissionRateForDeal(deal, staffUsers);
  // Out of the fund, never on top of it: the two rates always sum to
  // OPERATING_FUND_COMMISSION_PCT, so the shares account for exactly
  // 100% of margin whether or not a manager is named.
  const dispositionManagerRate = dispositionManagerCommissionRateForDeal(deal);
  const operatingFundRate = OPERATING_FUND_COMMISSION_PCT - dispositionManagerRate;
  return {
    // Both margins, because a commission card that showed only one
    // could not explain why the shares do not add up to the deal's
    // margin — and that difference is the fee.
    grossMargin,
    titleFee,
    closingCosts,
    closingCostsTotal: costsCharged,
    distributableMargin: totalMargin,
    buySide: buySplit.buySideAgent,
    buySidePool,
    coordinator: buySplit.coordinator,
    coordinatorRate: buySplit.coordinatorRate,
    coordinatorMarginBased: buySplit.coordinatorMarginBased,
    sellSide: totalMargin * sellSideRate,
    sellSideRate,
    investorRep: repTakesSellSide ? totalMargin * investorRepRate : 0,
    investorRepRate: repTakesSellSide ? investorRepRate : 0,
    investorRepId,
    sellSideReplacedByInvestorRep: repTakesSellSide,
    // Who the sell-side share is actually credited to, whichever slot it
    // landed in. The card and the invoice read this rather than
    // re-deriving it.
    sellSideAgentId: repTakesSellSide ? investorRepId : (deal?.sellSideAgentId || null),
    brad: totalMargin * 0.10,
    david: totalMargin * 0.10,
    mays: totalMargin * 0.10,
    dispositionManager: totalMargin * dispositionManagerRate,
    dispositionManagerRate,
    operatingFund: totalMargin * operatingFundRate,
    operatingFundRate,
  };
}

// Paraclete self-purchase. The buy side is a single 50%-of-margin pool
// instead of the standard 40%, and the coordinator is carved out of it
// exactly the way they're carved out of the 40% pool on an outside
// sale. Before this the Paraclete branch hard-zeroed the coordinator,
// so a deal carrying an explicit coordinator rate still paid them
// nothing — on screen, in the approval queue, and in the ledger.
//
// Every Paraclete-buyer call site routes through here so the commission
// card, the Commission Report PDF, the approval rows, the Accounting
// ledger and the materialized payouts cannot drift apart.
export function buildParacleteBuyerCommissionShares(margin, deal = {}, staffUsers = [], options = {}) {
  const grossMargin = Number(margin) || 0;
  // Every transaction, self-purchase included: the title work happens
  // either way.
  //
  // `titleFeeApplies: false` is for a caller dividing ONE transaction
  // between two pools — the fee is per transaction, not per pool, so the
  // caller takes it once and tells each pool it is already accounted for.
  // Without this, a mixed deal would be charged $400 for one title search.
  const { titleFee, closingCosts, closingCostsTotal: costsCharged, distributableMargin: totalMargin } = options.titleFeeApplies === false
    ? { titleFee: 0, closingCosts: [], closingCostsTotal: 0, distributableMargin: grossMargin }
    : titleFeeForMargin(grossMargin, deal);
  const pool = totalMargin * PARACLETE_BUY_SIDE_COMMISSION_PCT;
  const coordinatorRate = deal?.coordinatorId
    ? coordinatorCommissionRateForDeal(deal, staffUsers)
    : 0;
  // splitBuySideCommission clamps the carve-out to the pool, so a
  // coordinator rate above 50% cannot push the buy-side agent negative.
  const split = splitBuySideCommission(pool, coordinatorRate, {
    margin: totalMargin,
    marginBased: coordinatorMarginSplitApplies(deal),
  });
  return {
    grossMargin,
    titleFee,
    closingCosts,
    closingCostsTotal: costsCharged,
    distributableMargin: totalMargin,
    paracleteBuySidePool: pool,
    buySideAgent: split.buySideAgent,
    coordinator: split.coordinator,
    coordinatorRate: split.coordinatorRate,
    coordinatorMarginBased: split.coordinatorMarginBased,
  };
}

export function formatPercent(rate) {
  const value = Number(rate) || 0;
  return `${Math.round(value * 100)}%`;
}
